import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@roger/ui-components';
import {
  Check,
  ChevronDown,
  ChevronsUpDown,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { buildRogerAppUsername } from '../../utils/rogerAppUsername';
import { userTypeLabel } from '../../constants/userTypes';
import { useStore } from '../../store';
import {
  assignRogerDocAccountLink,
  clearRogerDocAccountLink,
  createRogerDocAccountLink,
  listRogerDocAccountLinks,
  type RogerDocClinicianAccount,
} from '../../services/accountLinkApi';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { memberDisplayName } from './grid/clinicSetup';
import type { OrgMemberResponse } from './types';

// ─── Helpers ────────────────────────────────────────────────────────────────

function accountName(account: RogerDocClinicianAccount): string {
  return [account.first_name, account.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
}

function accountLabel(account: RogerDocClinicianAccount): string {
  const name = accountName(account);
  const base = name ? `${account.username} (${name})` : account.username;
  return account.admin ? `${base} — admin` : base;
}

function compareAssignableAccounts(
  a: RogerDocClinicianAccount,
  b: RogerDocClinicianAccount
): number {
  const labelCompare = accountLabel(a).localeCompare(
    accountLabel(b),
    undefined,
    {
      sensitivity: 'base',
      numeric: true,
    }
  );
  if (labelCompare !== 0) return labelCompare;

  const emailCompare = (a.email ?? '').localeCompare(b.email ?? '', undefined, {
    sensitivity: 'base',
  });
  if (emailCompare !== 0) return emailCompare;

  return a.id.localeCompare(b.id);
}

function accountSearchFields(account: RogerDocClinicianAccount): string[] {
  return [
    account.username,
    account.first_name,
    account.last_name,
    account.email,
    account.clinician_title,
  ].filter((part): part is string => Boolean(part));
}

function accountMatchesSearch(
  account: RogerDocClinicianAccount,
  query: string
): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  return accountSearchFields(account).some(field =>
    field.toLowerCase().includes(normalizedQuery)
  );
}

/** Unlinked accounts for the current agency (API is scoped by agency.system_name). */
function isAssignableAccount(
  account: RogerDocClinicianAccount,
  agencyId: string | undefined
): boolean {
  if (!agencyId) return false;
  return !account.roger_emr;
}

// ─── Component ──────────────────────────────────────────────────────────────

const LINK_CLASS =
  'rounded px-1 text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50';

type RogerDocAccountSectionProps = {
  member: OrgMemberResponse;
  /** Called after a successful mutation so the parent can refresh its data. */
  onChanged?: () => void;
  /**
   * The list as shown here, after each link, unlink or reload. Given, it
   * replaces `onChanged` while mounted; a call that ends after unmount still
   * calls `onChanged`, since this list can no longer reach the parent.
   */
  onAccountsChange?: (accounts: RogerDocClinicianAccount[]) => void;
  /** False when the section has a tab of its own: always open, no toggle. */
  collapsible?: boolean;
  /** In a modal of its own: the clinic editor's box layout. */
  inDialog?: boolean;
  /**
   * The agency's account list the page already holds. Shown at once; a quiet
   * reload then checks it, since the list comes from Roger Docs and is slow.
   */
  initialAccounts?: RogerDocClinicianAccount[];
};

export const RogerDocAccountSection: React.FC<RogerDocAccountSectionProps> =
  React.memo(function RogerDocAccountSection({
    member,
    onChanged,
    onAccountsChange,
    collapsible = true,
    inDialog = false,
    initialAccounts,
  }) {
    const { agencyId, agencyAbbreviation, showSuccess, showError } = useStore(
      useShallow(state => ({
        agencyId: state.user?.agency?.id,
        agencyAbbreviation: state.user?.agency?.abbreviation,
        showSuccess: state.showSuccess,
        showError: state.showError,
      }))
    );

    const memberEmail = member.email.toLowerCase();
    // Only the dialog fills in a title; outside it the title starts blank.
    const defaultTitle =
      inDialog && member.user_type ? userTypeLabel(member.user_type) : '';

    const [accounts, setAccounts] = useState<RogerDocClinicianAccount[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    // A seeded list may be old, so linking waits for a load of this member's
    // own to answer: an account claimed since would otherwise be moved.
    const [answered, setAnswered] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const [accountPickerOpen, setAccountPickerOpen] = useState(false);
    const [accountSearchQuery, setAccountSearchQuery] = useState('');

    // Collapsed by default to leave more room for the role assignment UI.
    const [expanded, setExpanded] = useState(!collapsible);

    // Create-new-account form
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [firstName, setFirstName] = useState(member.first_name ?? '');
    const [lastName, setLastName] = useState(member.last_name ?? '');

    const derivedUsername = useMemo(
      () =>
        buildRogerAppUsername({
          firstName,
          lastName,
          agencyAbbreviation,
        }),
      [firstName, lastName, agencyAbbreviation]
    );
    const [clinicianTitle, setClinicianTitle] = useState('');
    const [username, setUsername] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    // The dialog's choice between a new account and an existing one, and
    // whether it is open beside an account they already have.
    const [chooserMode, setChooserMode] = useState<'create' | 'link'>('create');
    const [addingAnother, setAddingAnother] = useState(false);

    // Links changed here and not yet seen in a reload, so a reload answered
    // from before the change can't put the old state back. `quiet` is the
    // reload after a link or unlink, which keeps the picker off "Loading".
    const localLinksRef = useRef(
      new Map<string, RogerDocClinicianAccount['roger_emr']>()
    );
    const loadSeqRef = useRef(0);
    // Set before a change the parent should hear about; the seeded list is not.
    const reportRef = useRef(false);
    const mountedRef = useRef(true);
    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);
    // The visible load that owns the spinner. A load that loses the race to a
    // newer one must not clear the spinner while that one is still running.
    const loudSeqRef = useRef(0);
    const loadAccounts = useCallback(
      async ({ quiet = false } = {}): Promise<boolean> => {
        const seq = ++loadSeqRef.current;
        if (!quiet) {
          loudSeqRef.current = seq;
          setIsLoading(true);
        }
        try {
          // Backend scopes this list to the current agency's RogerDoc tenant
          // (agency.system_name) before returning clinician accounts.
          const fresh = await listRogerDocAccountLinks();
          // Only the newest reload may write.
          if (seq !== loadSeqRef.current) return true;
          const local = localLinksRef.current;
          reportRef.current = true;
          setAnswered(true);
          setLoadFailed(false);
          setAccounts(
            fresh.map(account => {
              if (!local.has(account.id)) return account;
              const mine = local.get(account.id) ?? null;
              const theirs = account.roger_emr ?? null;
              const agrees =
                (mine === null && theirs === null) ||
                (mine !== null &&
                  theirs !== null &&
                  mine.user_email.toLowerCase() ===
                    theirs.user_email.toLowerCase() &&
                  mine.roger_emr_agency_id === theirs.roger_emr_agency_id);
              // Once Roger Docs reports the change, stop holding it.
              if (agrees) local.delete(account.id);
              return agrees ? account : { ...account, roger_emr: mine };
            })
          );
          return true;
        } catch (err) {
          console.error('Failed to load RogerDoc accounts:', err);
          if (seq === loadSeqRef.current) setLoadFailed(true);
          showError('Unable to load Roger App accounts. Please try again.');
          return false;
        } finally {
          if (!quiet && seq === loudSeqRef.current) setIsLoading(false);
        }
      },
      [showError]
    );

    useEffect(() => {
      setAnswered(false);
      setLoadFailed(false);
      if (initialAccounts) {
        setAccounts(initialAccounts);
        void loadAccounts({ quiet: true });
      } else {
        void loadAccounts();
      }
      // Reset transient state when switching members. A section shown without
      // its toggle has no way to reopen, so it stays open.
      setExpanded(!collapsible);
      setSelectedAccountId('');
      setAccountPickerOpen(false);
      setAccountSearchQuery('');
      setShowCreateForm(false);
      setFirstName(member.first_name ?? '');
      setLastName(member.last_name ?? '');
      setClinicianTitle(defaultTitle);
      setUsername('');
      setChooserMode('create');
      setAddingAnother(false);
      // initialAccounts is read once per member: the page replaces that array
      // on every reload, and rerunning this would reset the form mid-edit.
    }, [
      loadAccounts,
      collapsible,
      member.first_name,
      member.last_name,
      member.email,
      defaultTitle,
    ]);

    useEffect(() => {
      if (!reportRef.current) return;
      reportRef.current = false;
      onAccountsChange?.(accounts);
    }, [accounts, onAccountsChange]);

    const linkedAccounts = useMemo(
      () =>
        accounts.filter(
          a =>
            a.roger_emr &&
            a.roger_emr.roger_emr_agency_id === agencyId &&
            a.roger_emr.user_email.toLowerCase() === memberEmail
        ),
      [accounts, agencyId, memberEmail]
    );

    /** Accounts kept out of the picker while their unlink is in flight. */
    const [unlinkingIds, setUnlinkingIds] = useState<ReadonlySet<string>>(
      new Set()
    );

    // Accounts with no link yet are safe to assign without stealing one
    // (admin accounts included).
    const assignableAccounts = useMemo(
      () =>
        accounts
          .filter(
            a => isAssignableAccount(a, agencyId) && !unlinkingIds.has(a.id)
          )
          .slice()
          .sort(compareAssignableAccounts),
      [accounts, agencyId, unlinkingIds]
    );

    const filteredAssignableAccounts = useMemo(
      () =>
        assignableAccounts.filter(a =>
          accountMatchesSearch(a, accountSearchQuery)
        ),
      [assignableAccounts, accountSearchQuery]
    );

    const selectedAccount = useMemo(
      () => assignableAccounts.find(a => a.id === selectedAccountId),
      [assignableAccounts, selectedAccountId]
    );

    const accountPickerLabel = useMemo(() => {
      if (loadFailed && !answered) return 'Accounts could not be loaded';
      if (isLoading || !answered) return 'Loading accounts…';
      if (assignableAccounts.length === 0) {
        return 'No unlinked accounts available';
      }
      if (selectedAccount) {
        return accountLabel(selectedAccount);
      }
      return 'Select an account…';
    }, [
      isLoading,
      answered,
      loadFailed,
      assignableAccounts.length,
      selectedAccount,
    ]);

    const refreshAfterChange = useCallback(async () => {
      const loaded = await loadAccounts({ quiet: true });
      // A failed reload can't carry the change, so the parent reloads instead.
      if (!onAccountsChange || !mountedRef.current || !loaded) onChanged?.();
    }, [loadAccounts, onChanged, onAccountsChange]);

    /** Accounts shown as linked while the link call is still in flight. */
    const [linkingIds, setLinkingIds] = useState<ReadonlySet<string>>(
      new Set()
    );
    const setRogerEmrLink = useCallback(
      (accountId: string, link: RogerDocClinicianAccount['roger_emr']) => {
        localLinksRef.current.set(accountId, link ?? null);
        reportRef.current = true;
        setAccounts(prev =>
          prev.map(a => (a.id === accountId ? { ...a, roger_emr: link } : a))
        );
      },
      []
    );
    // After a failure the call may still have landed, so drop the override and
    // let the next reload report the real state.
    const revertRogerEmrLink = useCallback(
      (accountId: string, link: RogerDocClinicianAccount['roger_emr']) => {
        localLinksRef.current.delete(accountId);
        reportRef.current = true;
        setAccounts(prev =>
          prev.map(a => (a.id === accountId ? { ...a, roger_emr: link } : a))
        );
      },
      []
    );

    // Both directions move the account at once and put it back if the call
    // fails. The reload afterwards is quiet: it only corrects the list if the
    // app reports something different.
    // Both report success, so the dialog keeps its form open after a failure.
    const handleAssign = useCallback(async (): Promise<boolean> => {
      // An account a reload found linked elsewhere is no longer selectable.
      const accountId = selectedAccount?.id;
      if (!accountId) return false;
      const before = accounts.find(a => a.id === accountId)?.roger_emr ?? null;
      setRogerEmrLink(accountId, {
        user_email: member.email,
        roger_emr_agency_id: agencyId ?? '',
      });
      setLinkingIds(prev => new Set(prev).add(accountId));
      setSelectedAccountId('');
      try {
        await assignRogerDocAccountLink(accountId, member.email);
        showSuccess('Roger App account linked');
        void refreshAfterChange();
        return true;
      } catch (err) {
        console.error('Failed to link RogerDoc account:', err);
        revertRogerEmrLink(accountId, before);
        if (!mountedRef.current) onChanged?.();
        showError('Failed to link Roger App account. Please try again.');
        return false;
      } finally {
        setLinkingIds(prev => {
          const next = new Set(prev);
          next.delete(accountId);
          return next;
        });
      }
    }, [
      selectedAccount,
      accounts,
      member.email,
      agencyId,
      setRogerEmrLink,
      revertRogerEmrLink,
      showSuccess,
      showError,
      refreshAfterChange,
    ]);

    const handleClear = useCallback(
      async (accountId: string) => {
        const before =
          accounts.find(a => a.id === accountId)?.roger_emr ?? null;
        setRogerEmrLink(accountId, null);
        setUnlinkingIds(prev => new Set(prev).add(accountId));
        try {
          await clearRogerDocAccountLink(accountId);
          showSuccess('Roger App account unlinked');
          void refreshAfterChange();
        } catch (err) {
          console.error('Failed to unlink RogerDoc account:', err);
          revertRogerEmrLink(accountId, before);
          if (!mountedRef.current) onChanged?.();
          showError('Failed to unlink Roger App account. Please try again.');
        } finally {
          setUnlinkingIds(prev => {
            const next = new Set(prev);
            next.delete(accountId);
            return next;
          });
        }
      },
      [
        accounts,
        setRogerEmrLink,
        revertRogerEmrLink,
        showSuccess,
        showError,
        refreshAfterChange,
      ]
    );

    const handleCreate = useCallback(async (): Promise<boolean> => {
      if (!firstName.trim() || !lastName.trim()) {
        showError('First and last name are required.');
        return false;
      }
      setIsCreating(true);
      try {
        const trimmedUsername = username.trim();
        const result = await createRogerDocAccountLink({
          user_email: member.email,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          clinician_title: clinicianTitle.trim(),
          // Omit when blank so RogerDoc derives the username from the name.
          username: trimmedUsername || undefined,
        });
        if (result.setup_email_sent === false) {
          showError(
            'Roger App account created and linked, but the setup email could not be sent. ' +
              'The clinician can use "Forgot Password" to get credentials.'
          );
        } else {
          showSuccess('Roger App account created and linked');
        }
        setShowCreateForm(false);
        // The dialog has no title box, so the next account keeps theirs.
        setClinicianTitle(defaultTitle);
        setUsername('');
        await refreshAfterChange();
        return true;
      } catch (err) {
        console.error('Failed to create RogerDoc account:', err);
        // Surface an actionable 4xx message (e.g. duplicate-name conflict);
        // fall back to a generic message for 5xx / network errors.
        showError(
          getBusinessErrorMessage(err) ??
            'Failed to create Roger App account. Please try again.'
        );
        return false;
      } finally {
        setIsCreating(false);
      }
    }, [
      firstName,
      lastName,
      clinicianTitle,
      username,
      member.email,
      defaultTitle,
      showSuccess,
      showError,
      refreshAfterChange,
    ]);

    const accountPicker = (
      <Popover
        open={accountPickerOpen}
        onOpenChange={open => {
          setAccountPickerOpen(open);
          if (!open) setAccountSearchQuery('');
        }}
        // In a dialog its scroll lock would swallow the wheel over the list.
        modal={inDialog}
      >
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='outline'
            role='combobox'
            aria-expanded={accountPickerOpen}
            aria-label='Link an existing Roger App account'
            disabled={isLoading || !answered || assignableAccounts.length === 0}
            className='w-full justify-between font-normal'
          >
            <span
              className={
                selectedAccount
                  ? 'truncate text-foreground'
                  : 'truncate text-muted-foreground'
              }
            >
              {accountPickerLabel}
            </span>
            {isLoading || (!answered && !loadFailed) ? (
              <Loader2 className='ml-2 h-4 w-4 shrink-0 animate-spin text-muted-foreground' />
            ) : (
              <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 text-muted-foreground' />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-[var(--radix-popover-trigger-width)] p-0'
          align='start'
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder='Search accounts…'
              className='text-xs'
              value={accountSearchQuery}
              onValueChange={setAccountSearchQuery}
            />
            <CommandList>
              <CommandEmpty className='py-2 text-center text-xs text-muted-foreground'>
                No matching accounts.
              </CommandEmpty>
              <CommandGroup>
                {filteredAssignableAccounts.map(account => (
                  <CommandItem
                    key={account.id}
                    value={account.id}
                    onSelect={() => {
                      setSelectedAccountId(account.id);
                      setAccountPickerOpen(false);
                      setAccountSearchQuery('');
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 shrink-0 ${
                        selectedAccountId === account.id
                          ? 'opacity-100'
                          : 'opacity-0'
                      }`}
                    />
                    <span className='truncate'>{accountLabel(account)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );

    if (inDialog) {
      const memberName = memberDisplayName(member);
      // The choice is shown when they have no account, and on request for the
      // unusual second one.
      const choosing = linkedAccounts.length === 0 || addingAnother;
      const needsName = !member.first_name?.trim() || !member.last_name?.trim();
      const createUsername = username || derivedUsername || '';
      const canSubmit =
        chooserMode === 'link'
          ? !!selectedAccount && answered
          : !!createUsername.trim() && !!firstName.trim() && !!lastName.trim();
      const submit = async () => {
        const done =
          chooserMode === 'link' ? await handleAssign() : await handleCreate();
        if (done) setAddingAnother(false);
      };

      return (
        <div className='space-y-4'>
          {linkedAccounts.length > 0 ? (
            <div className='space-y-2'>
              {linkedAccounts.map(account => (
                <div
                  key={account.id}
                  className='flex items-center justify-between gap-3 rounded-md border px-3 py-2'
                >
                  <span className='min-w-0 truncate text-sm'>
                    <span className='font-medium'>{account.username}</span>
                    {accountName(account) && (
                      <span className='ml-2 text-muted-foreground'>
                        {accountName(account)}
                      </span>
                    )}
                  </span>
                  {linkingIds.has(account.id) ? (
                    <span className='flex items-center gap-1 text-xs text-muted-foreground'>
                      <Loader2 className='h-3 w-3 animate-spin' />
                      Linking…
                    </span>
                  ) : (
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => handleClear(account.id)}
                    >
                      Unlink
                    </Button>
                  )}
                </div>
              ))}
              {!addingAnother && (
                <button
                  type='button'
                  className={LINK_CLASS}
                  onClick={() => setAddingAnother(true)}
                >
                  Link another account
                </button>
              )}
            </div>
          ) : (
            <p className='text-sm text-muted-foreground'>
              {isLoading
                ? 'Loading Roger App accounts…'
                : loadFailed
                  ? 'Roger App accounts could not be loaded. Close this and try again.'
                  : `${memberName} doesn't have a Roger App account yet.`}
            </p>
          )}

          {choosing && !isLoading && !loadFailed && (
            <div className='space-y-3'>
              <label className='flex items-start gap-2 text-sm'>
                <input
                  type='radio'
                  className='mt-1'
                  checked={chooserMode === 'create'}
                  onChange={() => setChooserMode('create')}
                />
                <span className='min-w-0 flex-1'>
                  <span className='block'>Create a new account</span>
                  {chooserMode === 'create' && (
                    <span className='mt-1 block space-y-2'>
                      {needsName && (
                        <span className='flex gap-2'>
                          <Input
                            inputSize='sm'
                            aria-label='First name'
                            placeholder='First name'
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                          />
                          <Input
                            inputSize='sm'
                            aria-label='Last name'
                            placeholder='Last name'
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                          />
                        </span>
                      )}
                      <Input
                        inputSize='sm'
                        aria-label='Roger App username'
                        value={createUsername}
                        onChange={e => setUsername(e.target.value)}
                      />
                      <span className='block text-xs text-muted-foreground'>
                        Filled in from their name and your agency code. Change
                        it if you need to.
                      </span>
                    </span>
                  )}
                </span>
              </label>
              <label className='flex items-start gap-2 text-sm'>
                <input
                  type='radio'
                  className='mt-1'
                  checked={chooserMode === 'link'}
                  disabled={assignableAccounts.length === 0}
                  onChange={() => setChooserMode('link')}
                />
                <span className='min-w-0 flex-1'>
                  <span className='block'>They already have one — link it</span>
                  {assignableAccounts.length === 0 ? (
                    <span className='mt-0.5 block text-xs text-muted-foreground'>
                      Every Roger App account in this agency is already linked.
                    </span>
                  ) : (
                    chooserMode === 'link' && (
                      <span className='mt-1 block'>{accountPicker}</span>
                    )
                  )}
                </span>
              </label>
              <div className='flex justify-end gap-2'>
                {addingAnother && (
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setAddingAnother(false)}
                    disabled={isCreating}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type='button'
                  size='sm'
                  onClick={() => void submit()}
                  disabled={!canSubmit || isCreating}
                >
                  {isCreating && (
                    <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
                  )}
                  {chooserMode === 'link' ? 'Link account' : 'Create account'}
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <Collapsible
        open={expanded}
        onOpenChange={collapsible ? setExpanded : undefined}
      >
        {collapsible && <Separator className='my-4' />}
        <CollapsibleTrigger
          disabled={!collapsible}
          className={
            collapsible
              ? 'group flex w-full items-center justify-between gap-2 rounded-md py-2 text-left hover:bg-muted/40'
              : 'flex w-full items-center justify-between gap-2 pb-2 text-left'
          }
        >
          <div className='flex min-w-0 items-center gap-2'>
            {collapsible && (
              <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180' />
            )}
            <h3 className='text-sm font-semibold'>Roger App Account</h3>
            {linkedAccounts.length > 0 && (
              <Badge variant='secondary' className='text-xs'>
                {linkedAccounts.length} linked
              </Badge>
            )}
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            {!expanded && !isLoading && linkedAccounts.length === 0 && (
              <span className='text-xs text-muted-foreground'>Not linked</span>
            )}
            {isLoading && (
              <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
            )}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent className='space-y-3 overflow-hidden pb-1 pt-1'>
          <p className='text-xs text-muted-foreground'>
            Link this user to one or more Roger App accounts so visits sync
            correctly. The Roger App remains the source of truth.
          </p>

          {/* Currently linked accounts */}
          {linkedAccounts.length > 0 ? (
            <div className='space-y-2'>
              {linkedAccounts.map(account => (
                <div
                  key={account.id}
                  className='flex items-center justify-between rounded-md border px-3 py-2'
                >
                  <div className='flex min-w-0 items-center gap-2'>
                    <Badge variant='secondary' className='text-xs'>
                      {account.username}
                    </Badge>
                    {accountName(account) && (
                      <span className='truncate text-sm text-muted-foreground'>
                        {accountName(account)}
                      </span>
                    )}
                  </div>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => handleClear(account.id)}
                  >
                    <Trash2 className='h-3.5 w-3.5' />
                    Unlink
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className='text-sm text-muted-foreground'>
              No Roger App account linked yet.
            </p>
          )}

          {/* Link an existing account */}
          <div className='flex items-end gap-2'>
            <div className='min-w-0 flex-1'>
              <label className='mb-1 block text-xs font-medium'>
                Link an existing Roger App account
              </label>
              {accountPicker}
            </div>
            <Button
              type='button'
              onClick={handleAssign}
              disabled={!selectedAccount || !answered}
            >
              Link
            </Button>
          </div>

          {/* Create a new account */}
          {showCreateForm ? (
            <div className='space-y-2 rounded-md border p-3'>
              <div className='flex items-center justify-between'>
                <h4 className='text-sm font-medium'>New Roger App user</h4>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setShowCreateForm(false)}
                  disabled={isCreating}
                >
                  <X className='h-3.5 w-3.5' />
                </Button>
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <div>
                  <label className='mb-1 block text-xs font-medium'>
                    First name
                  </label>
                  <Input
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder='First name'
                  />
                </div>
                <div>
                  <label className='mb-1 block text-xs font-medium'>
                    Last name
                  </label>
                  <Input
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder='Last name'
                  />
                </div>
              </div>
              <div>
                <label className='mb-1 block text-xs font-medium'>
                  Title (optional)
                </label>
                <Input
                  value={clinicianTitle}
                  onChange={e => setClinicianTitle(e.target.value)}
                  placeholder='e.g. RN'
                />
              </div>
              <div>
                <label className='mb-1 block text-xs font-medium'>
                  Username (optional)
                </label>
                <Input
                  value={username || derivedUsername || ''}
                  onChange={e => setUsername(e.target.value)}
                  placeholder='Auto-generated from name if blank'
                />
                {derivedUsername && !username && (
                  <p className='mt-1 text-xs text-muted-foreground'>
                    Filled in from their name and your agency code. Change it if
                    you need to.
                  </p>
                )}
              </div>
              <div className='flex justify-end'>
                <Button
                  type='button'
                  onClick={handleCreate}
                  disabled={isCreating}
                >
                  {isCreating && (
                    <Loader2 className='mr-1 h-4 w-4 animate-spin' />
                  )}
                  Create &amp; link
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setShowCreateForm(true)}
            >
              <Plus className='h-4 w-4' />
              Create new Roger App user
            </Button>
          )}
        </CollapsibleContent>
      </Collapsible>
    );
  });
